import { useState, useEffect, useMemo, useRef } from 'react';
import {
  getTemplateSet, updateTemplateSet, getCitiesByProject,
  type TemplateSet, type TemplateVariant, type KeywordItem,
  type DataRow,
} from '@/lib/api';
import type { AdDefinition, HeadlineSlot, DescriptionSlot, KeywordMatchType } from '@/types';
import { getEffectiveAds } from '@/types';
import { toast } from 'sonner';

const parsePastedLines = (text: string): string[] =>
  text.split(/[\n\r]+/).map(l => l.trim()).filter(Boolean);

export const addVariant = (variants: TemplateVariant[]): TemplateVariant[] => [
  ...variants, { variant_order: variants.length + 1, template_string: '', enabled: true },
];
export const removeVariant = (variants: TemplateVariant[], order: number): TemplateVariant[] =>
  variants.filter(v => v.variant_order !== order).map((v, i) => ({ ...v, variant_order: i + 1 }));
export const updateVariant = (variants: TemplateVariant[], order: number, updates: Partial<TemplateVariant>): TemplateVariant[] =>
  variants.map(v => v.variant_order === order ? { ...v, ...updates } : v);

export function useTemplateEditor(templateSetId: string | undefined) {
  const [templateSet, setTemplateSet] = useState<TemplateSet | null>(null);
  const [cities, setCities] = useState<DataRow[]>([]);
  const [availableFields, setAvailableFields] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [expandedVariants, setExpandedVariants] = useState<Record<string, boolean>>({});
  const [allVariantsExpanded, setAllVariantsExpanded] = useState(false);

  // Multi-ad
  const [activeAdIndex, setActiveAdIndex] = useState(1);
  const [showCopyMenu, setShowCopyMenu] = useState(false);
  const copyMenuRef = useRef<HTMLDivElement>(null);

  // Field palette
  const [activeInputId, setActiveInputId] = useState<string | null>(null);
  const activeInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const savedSelRef = useRef<{ start: number; end: number } | null>(null);

  // Summary row picker
  const [previewRowIdx, setPreviewRowIdx] = useState(0);
  const [rowSearch, setRowSearch] = useState('');
  const [showRowPicker, setShowRowPicker] = useState(false);
  const rowPickerRef = useRef<HTMLDivElement>(null);

  // ── Data loading ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!templateSetId) return;
    (async () => {
      try {
        setIsLoading(true);
        const data = await getTemplateSet(templateSetId);
        setTemplateSet(data);
        const citiesData = await getCitiesByProject(data.project_id);
        setCities(citiesData);
        if (citiesData.length > 0) {
          const stdFields = new Set<string>();
          const custFields = new Set<string>();
          citiesData.forEach(city => {
            Object.keys(city).forEach(k => {
              if (k !== 'id' && k !== 'project_id' && k !== 'custom_fields') stdFields.add(k);
            });
            if (city.custom_fields) Object.keys(city.custom_fields).forEach(k => custFields.add(k));
          });
          setAvailableFields([...Array.from(stdFields).sort(), ...Array.from(custFields).sort().map(f => `custom:${f}`)]);
        }
      } catch {
        toast.error('Failed to load template set');
      } finally {
        setIsLoading(false);
      }
    })();
  }, [templateSetId]);

  const handleSave = async () => {
    if (!templateSet) return;
    try {
      setIsSaving(true);
      await updateTemplateSet(templateSet.id, {
        name: templateSet.name,
        language_code: templateSet.language_code,
        generation_settings: templateSet.generation_settings,
        template_content: templateSet.template_content,
      });
      setHasUnsavedChanges(false);
      toast.success('Saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (hasUnsavedChanges && !isSaving) handleSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // templateSet is included so handleSave's closure always sees the latest content,
    // even when hasUnsavedChanges was already true on the previous render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnsavedChanges, isSaving, templateSet]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (rowPickerRef.current && !rowPickerRef.current.contains(e.target as Node)) {
        setShowRowPicker(false);
      }
      if (copyMenuRef.current && !copyMenuRef.current.contains(e.target as Node)) {
        setShowCopyMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Mutations ───────────────────────────────────────────────────────────────

  const updateTemplateContent = <K extends keyof TemplateSet['template_content']>(
    key: K, value: TemplateSet['template_content'][K]
  ) => {
    if (!templateSet) return;
    setTemplateSet({ ...templateSet, template_content: { ...templateSet.template_content, [key]: value } });
    setHasUnsavedChanges(true);
  };

  const updateHeadlineSlot = (slotNumber: number, variants: TemplateVariant[]) => {
    if (!templateSet) return;
    updateTemplateContent('headlines', templateSet.template_content.headlines.map(s =>
      s.slot_number === slotNumber ? { ...s, variants } : s
    ));
  };

  const updateHeadlineSlotProp = (slotNumber: number, updates: Partial<HeadlineSlot>) => {
    if (!templateSet) return;
    updateTemplateContent('headlines', templateSet.template_content.headlines.map(s =>
      s.slot_number === slotNumber ? { ...s, ...updates } : s
    ));
  };

  const updateDescriptionSlot = (slotNumber: number, variants: TemplateVariant[]) => {
    if (!templateSet) return;
    updateTemplateContent('descriptions', templateSet.template_content.descriptions.map(s =>
      s.slot_number === slotNumber ? { ...s, variants } : s
    ));
  };

  const updateDescriptionSlotProp = (slotNumber: number, updates: Partial<DescriptionSlot>) => {
    if (!templateSet) return;
    updateTemplateContent('descriptions', templateSet.template_content.descriptions.map(s =>
      s.slot_number === slotNumber ? { ...s, ...updates } : s
    ));
  };

  // ── Bulk paste ────────────────────────────────────────────────────────────────

  const pasteHeadlines = async (startSlot = 1) => {
    if (!templateSet) return;
    try {
      const text = await navigator.clipboard.readText();
      const lines = parsePastedLines(text);
      if (lines.length === 0) return;
      const maxSlot = 15;
      const updated = templateSet.template_content.headlines.map(slot => {
        const idx = slot.slot_number - startSlot;
        if (idx < 0 || idx >= lines.length || slot.slot_number > maxSlot) return slot;
        return { ...slot, variants: updateVariant(slot.variants.length > 0 ? slot.variants : [{ variant_order: 1, template_string: '', enabled: true }], 1, { template_string: lines[idx] }) };
      });
      updateTemplateContent('headlines', updated);
      toast.success(`Pasted ${Math.min(lines.length, maxSlot - startSlot + 1)} headline${lines.length > 1 ? 's' : ''}`);
    } catch { toast.error('Could not read clipboard'); }
  };

  const pasteDescriptions = async (startSlot = 1) => {
    if (!templateSet) return;
    try {
      const text = await navigator.clipboard.readText();
      const lines = parsePastedLines(text);
      if (lines.length === 0) return;
      const maxSlot = 4;
      const updated = templateSet.template_content.descriptions.map(slot => {
        const idx = slot.slot_number - startSlot;
        if (idx < 0 || idx >= lines.length || slot.slot_number > maxSlot) return slot;
        return { ...slot, variants: updateVariant(slot.variants.length > 0 ? slot.variants : [{ variant_order: 1, template_string: '', enabled: true }], 1, { template_string: lines[idx] }) };
      });
      updateTemplateContent('descriptions', updated);
      toast.success(`Pasted ${Math.min(lines.length, maxSlot - startSlot + 1)} description${lines.length > 1 ? 's' : ''}`);
    } catch { toast.error('Could not read clipboard'); }
  };

  const handleSlotPaste = (e: React.ClipboardEvent, type: 'headline' | 'description', slotNumber: number) => {
    const text = e.clipboardData.getData('text/plain');
    const lines = parsePastedLines(text);
    if (lines.length <= 1) return;
    e.preventDefault();
    if (type === 'headline') {
      const maxSlot = 15;
      const updated = templateSet!.template_content.headlines.map(slot => {
        const idx = slot.slot_number - slotNumber;
        if (idx < 0 || idx >= lines.length || slot.slot_number > maxSlot) return slot;
        return { ...slot, variants: updateVariant(slot.variants.length > 0 ? slot.variants : [{ variant_order: 1, template_string: '', enabled: true }], 1, { template_string: lines[idx] }) };
      });
      updateTemplateContent('headlines', updated);
      toast.success(`Pasted ${Math.min(lines.length, maxSlot - slotNumber + 1)} headlines from H${slotNumber}`);
    } else {
      const maxSlot = 4;
      const updated = templateSet!.template_content.descriptions.map(slot => {
        const idx = slot.slot_number - slotNumber;
        if (idx < 0 || idx >= lines.length || slot.slot_number > maxSlot) return slot;
        return { ...slot, variants: updateVariant(slot.variants.length > 0 ? slot.variants : [{ variant_order: 1, template_string: '', enabled: true }], 1, { template_string: lines[idx] }) };
      });
      updateTemplateContent('descriptions', updated);
      toast.success(`Pasted ${Math.min(lines.length, maxSlot - slotNumber + 1)} descriptions from D${slotNumber}`);
    }
  };

  // ── Multi-ad helpers ─────────────────────────────────────────────────────────

  const makeEmptySlots = (type: 'headline' | 'description'): (HeadlineSlot | DescriptionSlot)[] => {
    const count = type === 'headline' ? 15 : 4;
    return Array.from({ length: count }, (_, i) => ({
      slot_number: i + 1,
      variants: [{ variant_order: 1, template_string: '', enabled: true }],
    }));
  };

  const ensureAdsArray = (): AdDefinition[] => {
    if (!templateSet) return [];
    const tc = templateSet.template_content;
    if (tc.ads && tc.ads.length > 0) return tc.ads;
    return [{
      ad_index: 1,
      headlines: tc.headlines,
      descriptions: tc.descriptions,
      ...(tc.use_per_ad_urls ? { final_url_template: tc.final_url_template, path1_template: tc.path1_template, path2_template: tc.path2_template } : {}),
    }];
  };

  const currentAds = templateSet ? getEffectiveAds(templateSet.template_content) : [];
  const currentAd = currentAds.find(a => a.ad_index === activeAdIndex) ?? currentAds[0];
  const adCount = currentAds.length;
  const isMultiAd = adCount > 1;

  const activeHeadlines = currentAd?.headlines ?? templateSet?.template_content.headlines ?? [];
  const activeDescriptions = currentAd?.descriptions ?? templateSet?.template_content.descriptions ?? [];

  const updateAd = (adIndex: number, updates: Partial<AdDefinition>) => {
    if (!templateSet) return;
    const ads = ensureAdsArray().map(a => a.ad_index === adIndex ? { ...a, ...updates } : a);
    const ad1 = ads.find(a => a.ad_index === 1)!;
    setTemplateSet({
      ...templateSet,
      template_content: {
        ...templateSet.template_content,
        ads,
        headlines: ad1.headlines,
        descriptions: ad1.descriptions,
      },
    });
    setHasUnsavedChanges(true);
  };

  const addAd = (copyFromIndex?: number) => {
    if (!templateSet || adCount >= 3) return;
    const ads = ensureAdsArray();
    const newIndex = ads.length + 1;
    let newAd: AdDefinition;
    if (copyFromIndex) {
      const source = ads.find(a => a.ad_index === copyFromIndex);
      newAd = {
        ad_index: newIndex,
        headlines: JSON.parse(JSON.stringify(source?.headlines ?? makeEmptySlots('headline'))),
        descriptions: JSON.parse(JSON.stringify(source?.descriptions ?? makeEmptySlots('description'))),
        ...(templateSet.template_content.use_per_ad_urls && source ? {
          final_url_template: source.final_url_template,
          path1_template: source.path1_template,
          path2_template: source.path2_template,
        } : {}),
      };
    } else {
      newAd = {
        ad_index: newIndex,
        headlines: makeEmptySlots('headline') as HeadlineSlot[],
        descriptions: makeEmptySlots('description') as DescriptionSlot[],
      };
    }
    const updatedAds = [...ads, newAd];
    const ad1 = updatedAds.find(a => a.ad_index === 1)!;
    setTemplateSet({
      ...templateSet,
      template_content: {
        ...templateSet.template_content,
        ads: updatedAds,
        headlines: ad1.headlines,
        descriptions: ad1.descriptions,
      },
    });
    setHasUnsavedChanges(true);
    setActiveAdIndex(newIndex);
    toast.success(copyFromIndex ? `Ad ${newIndex} created (copied from Ad ${copyFromIndex})` : `Ad ${newIndex} created`);
  };

  const removeAd = (adIndex: number) => {
    if (!templateSet || adIndex === 1) return;
    const ads = ensureAdsArray()
      .filter(a => a.ad_index !== adIndex)
      .map((a, i) => ({ ...a, ad_index: i + 1 }));
    const ad1 = ads[0];
    setTemplateSet({
      ...templateSet,
      template_content: {
        ...templateSet.template_content,
        ads: ads.length === 1 ? undefined : ads,
        headlines: ad1.headlines,
        descriptions: ad1.descriptions,
      },
    });
    setHasUnsavedChanges(true);
    setActiveAdIndex(Math.min(activeAdIndex, ads.length));
    toast.success(`Ad removed`);
  };

  const updateActiveHeadlineSlot = (slotNumber: number, variants: TemplateVariant[]) => {
    if (!templateSet) return;
    if (isMultiAd || templateSet.template_content.ads) {
      const updated = activeHeadlines.map(s => s.slot_number === slotNumber ? { ...s, variants } : s);
      updateAd(activeAdIndex, { headlines: updated });
    } else {
      updateHeadlineSlot(slotNumber, variants);
    }
  };

  const updateActiveHeadlineSlotProp = (slotNumber: number, updates: Partial<HeadlineSlot>) => {
    if (!templateSet) return;
    if (isMultiAd || templateSet.template_content.ads) {
      const updated = activeHeadlines.map(s => s.slot_number === slotNumber ? { ...s, ...updates } : s);
      updateAd(activeAdIndex, { headlines: updated });
    } else {
      updateHeadlineSlotProp(slotNumber, updates);
    }
  };

  const updateActiveDescriptionSlot = (slotNumber: number, variants: TemplateVariant[]) => {
    if (!templateSet) return;
    if (isMultiAd || templateSet.template_content.ads) {
      const updated = activeDescriptions.map(s => s.slot_number === slotNumber ? { ...s, variants } : s);
      updateAd(activeAdIndex, { descriptions: updated });
    } else {
      updateDescriptionSlot(slotNumber, variants);
    }
  };

  const updateActiveDescriptionSlotProp = (slotNumber: number, updates: Partial<DescriptionSlot>) => {
    if (!templateSet) return;
    if (isMultiAd || templateSet.template_content.ads) {
      const updated = activeDescriptions.map(s => s.slot_number === slotNumber ? { ...s, ...updates } : s);
      updateAd(activeAdIndex, { descriptions: updated });
    } else {
      updateDescriptionSlotProp(slotNumber, updates);
    }
  };

  const pasteActiveHeadlines = async (startSlot = 1) => {
    if (!templateSet) return;
    try {
      const text = await navigator.clipboard.readText();
      const lines = parsePastedLines(text);
      if (lines.length === 0) return;
      const maxSlot = 15;
      const updated = activeHeadlines.map(slot => {
        const idx = slot.slot_number - startSlot;
        if (idx < 0 || idx >= lines.length || slot.slot_number > maxSlot) return slot;
        return { ...slot, variants: updateVariant(slot.variants.length > 0 ? slot.variants : [{ variant_order: 1, template_string: '', enabled: true }], 1, { template_string: lines[idx] }) };
      });
      if (isMultiAd || templateSet.template_content.ads) {
        updateAd(activeAdIndex, { headlines: updated });
      } else {
        updateTemplateContent('headlines', updated);
      }
      toast.success(`Pasted ${Math.min(lines.length, maxSlot - startSlot + 1)} headline${lines.length > 1 ? 's' : ''}`);
    } catch { toast.error('Could not read clipboard'); }
  };

  const pasteActiveDescriptions = async (startSlot = 1) => {
    if (!templateSet) return;
    try {
      const text = await navigator.clipboard.readText();
      const lines = parsePastedLines(text);
      if (lines.length === 0) return;
      const maxSlot = 4;
      const updated = activeDescriptions.map(slot => {
        const idx = slot.slot_number - startSlot;
        if (idx < 0 || idx >= lines.length || slot.slot_number > maxSlot) return slot;
        return { ...slot, variants: updateVariant(slot.variants.length > 0 ? slot.variants : [{ variant_order: 1, template_string: '', enabled: true }], 1, { template_string: lines[idx] }) };
      });
      if (isMultiAd || templateSet.template_content.ads) {
        updateAd(activeAdIndex, { descriptions: updated });
      } else {
        updateTemplateContent('descriptions', updated);
      }
      toast.success(`Pasted ${Math.min(lines.length, maxSlot - startSlot + 1)} description${lines.length > 1 ? 's' : ''}`);
    } catch { toast.error('Could not read clipboard'); }
  };

  // ── Keyword helpers ──────────────────────────────────────────────────────────

  const parseKeywordMatchType = (raw: string): { text: string; matchTypes: KeywordMatchType[] } => {
    const trimmed = raw.trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      return { text: trimmed, matchTypes: ['exact'] };
    }
    if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
      return { text: trimmed, matchTypes: ['phrase'] };
    }
    return { text: trimmed, matchTypes: ['broad'] };
  };

  const stripKeywordWrap = (text: string): string => {
    const t = text.trim();
    if ((t.startsWith('[') && t.endsWith(']')) || (t.startsWith('"') && t.endsWith('"'))) return t.slice(1, -1);
    return t;
  };

  const wrapKeyword = (text: string, matchType: KeywordMatchType): string => {
    const bare = stripKeywordWrap(text);
    if (matchType === 'exact') return `[${bare}]`;
    if (matchType === 'phrase') return `"${bare}"`;
    return bare;
  };

  const pasteKeywords = async () => {
    if (!templateSet) return;
    try {
      const text = await navigator.clipboard.readText();
      const lines = parsePastedLines(text);
      if (lines.length === 0) return;
      const newKeywords: KeywordItem[] = lines.map(line => {
        const parsed = parseKeywordMatchType(line);
        return {
          id: crypto.randomUUID(),
          item_name: '',
          match_types: parsed.matchTypes,
          variants: [{ variant_order: 1, template_string: parsed.text, enabled: true }],
        };
      });
      updateTemplateContent('keywords', [...templateSet.template_content.keywords, ...newKeywords]);
      toast.success(`Pasted ${lines.length} keyword${lines.length > 1 ? 's' : ''}`);
    } catch { toast.error('Could not read clipboard'); }
  };

  const handleKeywordPaste = (e: React.ClipboardEvent, keywordId: string) => {
    const text = e.clipboardData.getData('text/plain');
    const lines = parsePastedLines(text);
    if (lines.length <= 1) return;
    if (!templateSet) return;
    e.preventDefault();
    const firstParsed = parseKeywordMatchType(lines[0]);
    const updated = templateSet.template_content.keywords.map(k => {
      if (k.id !== keywordId) return k;
      return {
        ...k,
        match_types: firstParsed.matchTypes,
        variants: updateVariant(k.variants.length > 0 ? k.variants : [{ variant_order: 1, template_string: '', enabled: true }], 1, { template_string: firstParsed.text }),
      };
    });
    const newKeywords: KeywordItem[] = lines.slice(1).map(line => {
      const parsed = parseKeywordMatchType(line);
      return {
        id: crypto.randomUUID(),
        item_name: '',
        match_types: parsed.matchTypes,
        variants: [{ variant_order: 1, template_string: parsed.text, enabled: true }],
      };
    });
    updateTemplateContent('keywords', [...updated, ...newKeywords]);
    toast.success(`Pasted ${lines.length} keywords`);
  };

  const handleActiveSlotPaste = (e: React.ClipboardEvent, type: 'headline' | 'description', slotNumber: number) => {
    const text = e.clipboardData.getData('text/plain');
    const lines = parsePastedLines(text);
    if (lines.length <= 1) return;
    e.preventDefault();
    if (type === 'headline') {
      const maxSlot = 15;
      const updated = activeHeadlines.map(slot => {
        const idx = slot.slot_number - slotNumber;
        if (idx < 0 || idx >= lines.length || slot.slot_number > maxSlot) return slot;
        return { ...slot, variants: updateVariant(slot.variants.length > 0 ? slot.variants : [{ variant_order: 1, template_string: '', enabled: true }], 1, { template_string: lines[idx] }) };
      });
      if (isMultiAd || templateSet?.template_content.ads) {
        updateAd(activeAdIndex, { headlines: updated });
      } else {
        updateTemplateContent('headlines', updated);
      }
      toast.success(`Pasted ${Math.min(lines.length, maxSlot - slotNumber + 1)} headlines from H${slotNumber}`);
    } else {
      const maxSlot = 4;
      const updated = activeDescriptions.map(slot => {
        const idx = slot.slot_number - slotNumber;
        if (idx < 0 || idx >= lines.length || slot.slot_number > maxSlot) return slot;
        return { ...slot, variants: updateVariant(slot.variants.length > 0 ? slot.variants : [{ variant_order: 1, template_string: '', enabled: true }], 1, { template_string: lines[idx] }) };
      });
      if (isMultiAd || templateSet?.template_content.ads) {
        updateAd(activeAdIndex, { descriptions: updated });
      } else {
        updateTemplateContent('descriptions', updated);
      }
      toast.success(`Pasted ${Math.min(lines.length, maxSlot - slotNumber + 1)} descriptions from D${slotNumber}`);
    }
  };

  // ── Derived ─────────────────────────────────────────────────────────────────

  const tp = templateSet?.template_content.template_placeholders ?? {};

  const selectedFields = templateSet?.template_content.selected_fields || [];

  const renderWithRow = (template: string, row: Record<string, any> | null): string => {
    if (!row || !template) return template ?? '';
    return template.replace(/\{([^}]+)\}/g, (match, fieldName) => {
      const value = fieldName.startsWith('custom:')
        ? row.custom_fields?.[fieldName.replace('custom:', '')]
        : row[fieldName];
      if (value !== undefined) return String(value);
      const tpVal = templateSet?.template_content.template_placeholders?.[fieldName];
      if (tpVal !== undefined && tpVal !== null) return String(tpVal);
      return match;
    });
  };

  const sampleDataRow = cities[0] ?? null;
  const previewRow = cities[previewRowIdx] ?? sampleDataRow;
  const getPreviewText = (template: string) => renderWithRow(template, previewRow);

  // Char count = rendered length using the current preview row (ground truth: matches what
  // ships to Google Ads). With no preview row available, fall back to substituting template
  // placeholders only and reporting `remaining` as a budget hint for any DB placeholders.
  const getCharInfo = (text: string, limit: number): { count: number; remaining?: number } => {
    if (previewRow) {
      return { count: renderWithRow(text, previewRow).length };
    }
    let hasDbPlaceholder = false;
    const resolved = text.replace(/\{([^}]+)\}/g, (_match, key) => {
      const tpVal = tp[key];
      if (tpVal !== undefined) return tpVal;
      hasDbPlaceholder = true;
      return '';
    });
    const count = resolved.length;
    return hasDbPlaceholder ? { count, remaining: Math.max(0, limit - count) } : { count };
  };

  const splitStructureStats = useMemo(() => {
    if (!templateSet || cities.length === 0) return null;
    const primaryField = templateSet.template_content.primary_grouping_field || templateSet.template_content.campaign_grouping_field || 'none';
    const splitField = templateSet.template_content.secondary_grouping_field;
    if (!splitField) return null;
    const getVal = (row: DataRow, field: string): string => {
      if (field === 'none') return 'All';
      if (field.startsWith('custom:')) return String(row.custom_fields?.[field.replace('custom:', '')] ?? 'Unknown');
      return String((row as any)[field] ?? 'Unknown');
    };
    const primaryValues = new Set(cities.map(c => getVal(c, primaryField)));
    const splitValues = new Set(cities.map(c => getVal(c, splitField)));
    const campaignPairs = new Set(cities.map(c => `${getVal(c, primaryField)}||${getVal(c, splitField)}`));
    return {
      primaryCount: primaryValues.size,
      splitCount: splitValues.size,
      campaignCount: campaignPairs.size,
      primaryLabel: primaryField === 'none' ? 'all rows' : primaryField.replace('custom:', ''),
      splitLabel: splitField.replace('custom:', ''),
      splitPlaceholder: `{${splitField}}`,
    };
  }, [templateSet?.template_content.primary_grouping_field, templateSet?.template_content.campaign_grouping_field, templateSet?.template_content.secondary_grouping_field, cities]);

  // ── Field palette ────────────────────────────────────────────────────────────

  const inputProps = (id: string) => ({
    onFocus: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setActiveInputId(id);
      activeInputRef.current = e.currentTarget;
    },
  });

  const handleChipMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    if (activeInputRef.current) {
      savedSelRef.current = {
        start: activeInputRef.current.selectionStart ?? activeInputRef.current.value.length,
        end: activeInputRef.current.selectionEnd ?? activeInputRef.current.value.length,
      };
    }
  };

  const handleFieldInsert = (field: string) => {
    if (!activeInputId || !activeInputRef.current || !templateSet) {
      toast.info('Click on an input first, then insert a field');
      return;
    }
    const token = `{${field}}`;
    const el = activeInputRef.current;
    const start = savedSelRef.current?.start ?? el.value.length;
    const end = savedSelRef.current?.end ?? el.value.length;
    const newValue = el.value.slice(0, start) + token + el.value.slice(end);
    const parts = activeInputId.split(':');
    const type = parts[0];
    if (type === 'h') {
      const slotNum = parseInt(parts[1]);
      const varOrder = parseInt(parts[2]);
      const slot = templateSet.template_content.headlines.find(s => s.slot_number === slotNum)!;
      updateHeadlineSlot(slotNum, updateVariant(slot.variants, varOrder, { template_string: newValue }));
    } else if (type === 'd') {
      const slotNum = parseInt(parts[1]);
      const varOrder = parseInt(parts[2]);
      const slot = templateSet.template_content.descriptions.find(s => s.slot_number === slotNum)!;
      updateDescriptionSlot(slotNum, updateVariant(slot.variants, varOrder, { template_string: newValue }));
    } else if (type === 'k') {
      const kwId = parts[1];
      const varOrder = parseInt(parts[2]);
      updateTemplateContent('keywords', templateSet.template_content.keywords.map(k => {
        if (k.id !== kwId) return k;
        return { ...k, variants: updateVariant(k.variants, varOrder, { template_string: newValue }) };
      }));
    } else {
      updateTemplateContent(activeInputId as any, newValue);
    }
    requestAnimationFrame(() => {
      if (activeInputRef.current) {
        activeInputRef.current.focus();
        const cur = start + token.length;
        activeInputRef.current.setSelectionRange(cur, cur);
      }
    });
  };

  const insertPlaceholder = (currentValue: string, placeholder: string) =>
    currentValue + `{${placeholder}}`;

  // ── Summary row search ───────────────────────────────────────────────────────

  const filteredCities = useMemo(() => {
    if (!rowSearch.trim()) return cities.slice(0, 10);
    const q = rowSearch.toLowerCase();
    return cities.filter(city =>
      Object.entries(city).some(([key, val]) => {
        if (key === 'custom_fields' && val && typeof val === 'object') {
          return Object.values(val).some(cv => String(cv ?? '').toLowerCase().includes(q));
        }
        return typeof val === 'string' && val.toLowerCase().includes(q);
      })
    ).slice(0, 10);
  }, [rowSearch, cities]);

  // Index lookup: avoid calling `cities.indexOf(city)` per dropdown row, which is O(n) on
  // a 34k-row source table. Built once per `cities` change.
  const cityIndexMap = useMemo(() => {
    const m = new Map<DataRow, number>();
    cities.forEach((c, i) => m.set(c, i));
    return m;
  }, [cities]);

  const rowDisplayLabel = (row: DataRow) => {
    if (selectedFields.length === 0) return 'Row';
    const vals = selectedFields.slice(0, 2).map(f => {
      const v = f.startsWith('custom:') ? row.custom_fields?.[f.replace('custom:', '')] : (row as any)[f];
      return String(v ?? '');
    }).filter(Boolean);
    return vals.join(' · ') || 'Row';
  };

  const rowMatchHint = (row: DataRow, query: string): string | null => {
    if (!query.trim()) return null;
    const q = query.toLowerCase();
    const label = rowDisplayLabel(row).toLowerCase();
    if (label.includes(q)) return null;
    for (const [key, val] of Object.entries(row)) {
      if (key === 'custom_fields' && val && typeof val === 'object') {
        for (const cv of Object.values(val)) {
          const s = String(cv ?? '');
          if (s.toLowerCase().includes(q)) return s;
        }
        continue;
      }
      if (typeof val === 'string' && val.toLowerCase().includes(q)) return val;
    }
    return null;
  };

  return {
    // state
    templateSet, setTemplateSet,
    cities, availableFields, selectedFields,
    isLoading, isSaving, hasUnsavedChanges,
    expandedVariants, setExpandedVariants, allVariantsExpanded, setAllVariantsExpanded,
    activeAdIndex, setActiveAdIndex,
    showCopyMenu, setShowCopyMenu, copyMenuRef,
    activeInputId, activeInputRef, savedSelRef,
    previewRowIdx, setPreviewRowIdx,
    rowSearch, setRowSearch,
    showRowPicker, setShowRowPicker,
    rowPickerRef,
    // mutations
    handleSave,
    updateTemplateContent,
    updateHeadlineSlot, updateHeadlineSlotProp,
    updateDescriptionSlot, updateDescriptionSlotProp,
    pasteHeadlines, pasteDescriptions,
    handleSlotPaste, handleKeywordPaste, handleActiveSlotPaste,
    addAd, removeAd, updateAd,
    currentAds, currentAd, adCount, isMultiAd,
    activeHeadlines, activeDescriptions,
    updateActiveHeadlineSlot, updateActiveHeadlineSlotProp,
    updateActiveDescriptionSlot, updateActiveDescriptionSlotProp,
    pasteActiveHeadlines, pasteActiveDescriptions,
    pasteKeywords,
    parseKeywordMatchType, stripKeywordWrap, wrapKeyword,
    // derived
    splitStructureStats, filteredCities, cityIndexMap,
    sampleDataRow, previewRow,
    renderWithRow, getPreviewText, getCharInfo,
    inputProps, handleChipMouseDown, handleFieldInsert, insertPlaceholder,
    rowDisplayLabel, rowMatchHint,
  };
}

export type TemplateEditorState = ReturnType<typeof useTemplateEditor>;
